import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";

import StoryExperience from "@/components/StoryExperience";
import { listExperiences } from "@/lib/experiences.functions";
import { getCategoryTree } from "@/lib/categories.functions";

const CATEGORY_LABELS: Record<string, string> = {
  suites: "Luxury Suites",
  culinary: "Dining & Culinary",
  "things-to-do": "Things to Do",
  onsite: "On-Site Activities",
  nearby: "Nearby Experiences",
  enhance: "Enhance Your Stay",
  "groups-events": "Groups & Events",
  intro: "Welcome",
};

const PATH_LABELS: Record<string, string> = {
  couples: "Couples",
  family: "Families",
  groups: "Groups",
};

function pretty(slug: string, dict: Record<string, string>) {
  if (dict[slug]) return dict[slug];
  return slug
    .split("-")
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(" ");
}

export const Route = createFileRoute("/p/$path/$slug")({
  head: ({ params }) => {
    const cat = pretty(params.slug, CATEGORY_LABELS);
    const path = pretty(params.path, PATH_LABELS);
    const title = `${cat} for ${path} — Backland`;
    const description = `${cat} at Backland — a luxury tented resort in Arizona's ponderosa pine high country, tailored for ${path.toLowerCase()}.`;
    const url = `https://www.travelbackland.com/p/${params.path}/${params.slug}`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "robots", content: "noindex, follow" },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: PathCategoryDeepLink,
});

function PathCategoryDeepLink() {
  const { path, slug } = Route.useParams();
  const fetchExps = useServerFn(listExperiences);
  const fetchTree = useServerFn(getCategoryTree);

  const { data: experiences } = useQuery({
    queryKey: ["experiences"],
    queryFn: () => fetchExps(),
    staleTime: 60_000,
  });
  const { data: tree } = useQuery({
    queryKey: ["category-tree"],
    queryFn: () => fetchTree(),
    staleTime: 60_000,
  });

  if (!experiences || !tree) {
    return <div className="fixed inset-0 bg-background" />;
  }

  const expMatch = experiences.find((e) => e.slug === path && e.include_in_picker);

  if (expMatch && typeof window !== "undefined") {
    try {
      localStorage.setItem("backland.experience", expMatch.slug);
    } catch {
      // ignore
    }
  }

  if (!expMatch) {
    return (
      <StoryExperience
        chooserError={`"${path}" isn't a valid experience. Please choose one below.`}
      />
    );
  }

  const catMatch =
    slug === "onsite" || slug === "nearby"
      ? true
      : tree.categories.some((c) => c.slug === slug);

  if (!catMatch) {
    return <StoryExperience />;
  }

  return <StoryExperience initialCategorySlug={slug} />;
}
